import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProfileEditComponent } from '@app/components/profile-edit/profile-edit.component';
import { patchPersonalDataFormGroup } from '@app/helpers/personal-data-form-patch.helper';
import { formatRoleLabel } from '@app/helpers/formatName.helper';
import { simplePhoneValidator } from '@app/helpers/validation.helper';
import { Person, UserAccount } from '@app/models/account.model';
import { Role } from '@app/models/admin.model';
import {
  DetailsFormControls,
  DetailsFormGroup,
  PersonalDataFormGroup,
} from '@app/models/form.models';
import { getPersonalDataFormGroup } from '@app/pages/registration-wizard/registration-wizard-form.helper';
import { UserService } from '@app/services/admin/user.service';
import { ToastService } from '@app/services/toast.service';
import { GenericStore } from '@app/stores/generic.store';
import { ValidatedCheckboxGroupComponent } from '@app/ui-components/validated-checkbox-group/validated-checkbox-group.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, Observable, tap, throwError } from 'rxjs';

export function getDetailsFormGroup(fb: FormBuilder): DetailsFormGroup {
  const base = getPersonalDataFormGroup(fb);

  return new FormGroup<DetailsFormControls>({
    ...base.controls,
    roles: fb.nonNullable.control<string[]>([], {
      validators: [Validators.required],
    }),
    defaultLanguageCode: fb.nonNullable.control<string>(
      '',
      Validators.required
    ),
    phoneNumber: fb.nonNullable.control<string>('', {
      validators: [simplePhoneValidator()],
    }),
  });
}

const roleKey = (name: string): string =>
  name ? name[0].toLowerCase() + name.slice(1) : '';

@Component({
  selector: 'app-details-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ProfileEditComponent,
    ValidatedCheckboxGroupComponent,
    ValidatedDropdownComponent,
    ValidatedInputComponent,
  ],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss',
})
export class DetailsTabComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  protected genericStore = inject(GenericStore);

  @Input() personalData!: UserAccount | null;
  @Input() userId!: string;

  formGroup!: DetailsFormGroup;

  rolesList: Role[] = [];

  private _rolesArray = new FormArray<FormControl<boolean>>([]);
  get rolesArray(): FormArray<FormControl<boolean>> {
    return this._rolesArray;
  }

  private _roleItems: ReadonlyArray<{ id: string; label: string }> = [];
  get rolesItems(): ReadonlyArray<{ id: string; label: string }> {
    return this._roleItems;
  }

  get personalFormGroup(): FormGroup<PersonalDataFormGroup> {
    return this.formGroup as unknown as FormGroup<PersonalDataFormGroup>;
  }

  constructor(private fb: FormBuilder, private userService: UserService) {}

  ngOnInit(): void {
    this.formGroup = getDetailsFormGroup(this.fb);

    toObservable(this.genericStore.roleEntities, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((roles) => this.applyRoles(roles ?? []));

    toObservable(this.genericStore.nameTitlesEntities, {
      injector: this.injector,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.formGroup) {
          this.loadFromApi();
        }
      });

    this.loadFromApi();
  }

  ngOnChanges(): void {
    if (this.formGroup) this.loadFromApi();
  }

  private applyRoles(roles: Role[]): void {
    const selectedNames = this.formGroup?.controls.roles.value ?? [];

    this.rolesList = roles ?? [];
    this._rolesArray.clear();
    this.rolesList.forEach(() =>
      this._rolesArray.push(this.fb.nonNullable.control<boolean>(false))
    );

    this._rolesArray.controls.forEach(
      (roleControl: FormControl<boolean>, roleIndex: number) => {
        const isSelected = selectedNames.includes(
          this.rolesList[roleIndex].name
        );
        roleControl.setValue(isSelected, { emitEvent: false });
      }
    );

    this._rolesArray.controls.forEach((roleControl) => {
      roleControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.syncSelectedRoles());
    });

    this._roleItems = this.rolesList.map((role) => {
      const key = `admin.users.roles.${roleKey(role.name)}`;
      const label = this.translate.instant(key) || formatRoleLabel(role.name);
      return { id: role.name, label };
    });

    this.syncSelectedRoles();
  }

  private syncSelectedRoles(): void {
    const selected = this._rolesArray.controls
      .map((roleControl: FormControl<boolean>, roleIndex: number) =>
        roleControl.value ? this.rolesList[roleIndex]?.name : null
      )
      .filter((name): name is string => !!name);

    this.formGroup.controls.roles.setValue(selected, { emitEvent: false });
    this.formGroup.controls.roles.updateValueAndValidity({ emitEvent: false });
  }

  private loadFromApi(): void {
    const user = this.personalData;
    if (!user) return;

    const incomingRoles: string[] = Array.isArray(user.roles) ? user.roles : [];
    this.formGroup.controls.roles.setValue(incomingRoles, {
      emitEvent: false,
    });

    if (this.rolesList.length) {
      this._rolesArray.controls.forEach(
        (roleControl: FormControl<boolean>, roleIndex: number) => {
          const isSelected = incomingRoles.includes(
            this.rolesList[roleIndex].name
          );
          roleControl.setValue(isSelected, { emitEvent: false });
        }
      );
    }
    this.syncSelectedRoles();

    this.formGroup.controls.defaultLanguageCode.setValue(
      user?.defaultLanguageCode ?? '',
      { emitEvent: false }
    );
    this.formGroup.controls.phoneNumber?.setValue(user?.phoneNumber ?? '', {
      emitEvent: false,
    });

    const personalData: Person | null = (user?.personalData ??
      null) as Person | null;
    if (!personalData) return;

    const nameTitles = this.genericStore.nameTitlesEntities();
    if (nameTitles.length > 0) {
      patchPersonalDataFormGroup(this.personalFormGroup, personalData, nameTitles);
      return;
    }

    this.personalFormGroup.patchValue({
      familyName: personalData.lastName ?? '',
      middleName: personalData.midName ?? '',
      givenName: personalData.firstName ?? '',
      citizenship: personalData.citizenship ?? '',
      birthLastName: personalData.maidenLastName ?? '',
      birthMiddleName: personalData.maidenMidName ?? '',
      birthFirstName: personalData.maidenFirstName ?? '',
      birthPlace: personalData.birthPlace ?? '',
      dateOfBirth: personalData.birthDate ?? '',
      motherLastName: personalData.motherLastName ?? '',
      motherMiddleName: personalData.motherMidName ?? '',
      motherFirstName: personalData.motherFirstName ?? '',
      certificateType: personalData.idCardType ?? null,
      numberOfCertificate: personalData.idCardNumber ?? '',
      expiryDate: personalData.idCardNumberExpiry ?? '',
      country: personalData.address?.countryCode ?? '',
      postalCode: personalData.address?.zipCode ?? '',
      city: personalData.address?.city ?? '',
      publicSpace: personalData.address?.publicSpace ?? '',
      publicSpaceType: personalData.address?.publicSpaceType ?? '',
      houseNumber: personalData.address?.houseNumber ?? '',
      doorOrFloor: personalData.address?.doorOrFloor ?? '',
      optionalTaxNumber: personalData.taxIdentifier ?? '',
      optionalBankNumber: personalData.bankAccountNumber ?? '',
    });
  }

  save(): Observable<void> | null {
    this.syncSelectedRoles();

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.rolesArray.markAllAsTouched();
      return null;
    }

    const formValue = this.formGroup.getRawValue();

    const editable = {
      roles: formValue.roles,
      defaultLanguageCode: formValue.defaultLanguageCode,
      phoneNumber: formValue.phoneNumber ?? null,
      personalData: {
        titleCode: formValue.title,
        lastName: formValue.familyName,
        midName: formValue.middleName,
        firstName: formValue.givenName,
        citizenship: formValue.citizenship,
        maidenLastName: formValue.birthLastName,
        maidenMidName: formValue.birthMiddleName,
        maidenFirstName: formValue.birthFirstName,
        birthPlace: formValue.birthPlace,
        birthDate: formValue.dateOfBirth,
        motherLastName: formValue.motherLastName,
        motherMidName: formValue.motherMiddleName,
        motherFirstName: formValue.motherFirstName,
        idCardType: formValue.certificateType,
        idCardNumber: formValue.numberOfCertificate,
        idCardNumberExpiry: formValue.expiryDate,
        taxIdentifier: formValue.optionalTaxNumber,
        bankAccountNumber: formValue.optionalBankNumber,
        address: {
          countryCode: formValue.country,
          zipCode: formValue.postalCode,
          city: formValue.city,
          publicSpace: formValue.publicSpace,
          publicSpaceType: formValue.publicSpaceType,
          houseNumber: formValue.houseNumber,
          doorOrFloor: formValue.doorOrFloor,
        },
      },
    };

    const mergedPayload = {
      ...this.personalData,
      ...editable,
      personalData: {
        ...this.personalData?.personalData,
        ...editable.personalData,
      },
    };

    const {
      id,
      userName,
      deleted,
      emailConfirmed,
      twoFactorEnabled,
      lockoutEnd,
      lockoutEnabled,
      phoneNumberConfirmed,
      verified,
      ...trimmed
    } = mergedPayload;

    const userIncomes = this.personalData?.notQualifiedUserIncomes ?? null;

    const requestBody = {
      ...trimmed,
      phoneNumber: editable.phoneNumber || null,
      notQualifiedUserIncomes: userIncomes ? JSON.stringify(userIncomes) : null,
      incomes: userIncomes?.incomes ?? null,
      availableMoneyAssetsValue:
        userIncomes?.availableMoneyAssetsValue ??
        (trimmed as any).availableMoneyAssetsValue ??
        null,
      financialCommitmentsValue:
        userIncomes?.financialCommitmentsValue ??
        (trimmed as any).financialCommitmentsValue ??
        null,
    };

    return this.userService.updateUser(this.userId, requestBody).pipe(
      tap(() =>
        this.toast.success(
          this.translate.instant('admin.toasts.savedSuccessfully')
        )
      ),
      catchError((err) => {
        this.toast.error(this.translate.instant('admin.toasts.saveFailed'));
        return throwError(() => err);
      })
    ) as unknown as Observable<void>;
  }

  cancel(): void {
    this.formGroup.reset();
    this._rolesArray.reset();
    this.loadFromApi();
  }
}
